import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardBody } from "reactstrap";
import { AsyncButton } from "../../common/buttons";
import SaveStatusBadge from "./SaveStatusBadge";
import VerticalStepper from "./VerticalStepper";
import WizardBottomNav from "./WizardBottomNav";
import useInstallationAutosave from "./useInstallationAutosave";
import useInstallationDvrs from "./useInstallationDvrs";
import {
  findFallbackStep,
  getAdvanceBlocker,
  getVisibleSteps,
  isAfterInSequence,
  resolveBranch,
  resolvePersistedStepId,
} from "./wizardSteps";
import { breadcrumbStepChange } from "./shared/wizardObservability";

import CrmInfoPanel from "./panels/CrmInfoPanel";
import InstallationInfoPanel from "./panels/InstallationInfoPanel";
import VideoRecorderPanel from "./panels/VideoRecorderPanel";
import AssetSelectionPanel from "./panels/AssetSelectionPanel";
import ShippingStatusPanel from "./panels/ShippingStatusPanel";
import RouterInfoPanel from "./panels/RouterInfoPanel";
import PortForwardingPanel from "./panels/PortForwardingPanel";
import DdnsPanel from "./panels/DdnsPanel";
import RtspConfigPanel from "./panels/RtspConfigPanel";
import ChannelSelectionPanel from "./panels/ChannelSelectionPanel";
import WaitingForVeesionPanel from "./panels/WaitingForVeesionPanel";
import UserCreationPanel from "./panels/UserCreationPanel";
import SummaryPanel from "./panels/SummaryPanel";

/**
 * Common prop shape passed to every step panel.
 *
 * @typedef {Object} StepPanelProps
 * @property {Object} installation The current installation object.
 * @property {(key: string, value: *) => void} setField Persist a field value (debounced).
 * @property {(key: string, value: *) => Promise<void>} commitField Persist immediately, returns the flush promise.
 * @property {(patch: Object) => void} mergeServerFields Merge server fields without scheduling a PATCH.
 * @property {() => Promise<void>} flushPending Drain queued + in-flight autosaves; await before side-door POSTs.
 * @property {ReturnType<typeof import("./useInstallationDvrs").default>} dvrStore DVR list + mutators.
 * @property {Object} draftFields Non-persisted UI-only field values.
 * @property {(key: string, value: *) => void} setDraftField Update a draft value.
 * @property {import("react").ReactNode} status Save status node.
 * @property {import("react").ReactNode} bottomNav Back/Next navigation node.
 */

const STEP_COMPONENTS = {
  start: CrmInfoPanel,
  scan: InstallationInfoPanel,
  dvr: VideoRecorderPanel,
  preparation: AssetSelectionPanel,
  logistics: ShippingStatusPanel,
  router: RouterInfoPanel,
  port_forwarding: PortForwardingPanel,
  ddns: DdnsPanel,
  rtsp_config: RtspConfigPanel,
  channel_selection: ChannelSelectionPanel,
  waiting_for_veesion: WaitingForVeesionPanel,
  user_creation: UserCreationPanel,
  completed: SummaryPanel,
};

// Steps whose Next button doubles as the step-completion CTA.
function buildCustomNextButton(stepId, { onClick, disabled }) {
  if (stepId === "waiting_for_veesion") {
    return (
      <AsyncButton color="success" onClick={onClick} disabled={disabled}>
        <i className="fas fa-check mr-2" />
        Quality Control Validated
      </AsyncButton>
    );
  }
  if (stepId === "user_creation") {
    return (
      <AsyncButton color="success" onClick={onClick} disabled={disabled}>
        <i className="fas fa-check mr-2" />
        Mark as Done
      </AsyncButton>
    );
  }
  return null;
}

function WizardCard({ installation: initialInstallation }) {
  const { installation, setField, commitField, mergeServerFields, flushPending, saveStatus } =
    useInstallationAutosave(initialInstallation);
  const dvrStore = useInstallationDvrs(initialInstallation);

  const branch = useMemo(() => resolveBranch(installation), [installation]);
  const steps = useMemo(() => getVisibleSteps(branch), [branch]);
  // `persistedStepId` reflects the saved `installation.step`; `viewedStepId`
  // is the on-screen step (drifts during review).
  const persistedStepId = useMemo(
    () => resolvePersistedStepId(installation, steps, branch),
    [installation, steps, branch]
  );
  // A persisted `channel_selection` step means config isn't applied yet, so the
  // probe results that seed the grids were lost on reload — open the probe step instead.
  const [viewedStepId, setViewedStepId] = useState(
    persistedStepId === "channel_selection" ? "rtsp_config" : persistedStepId
  );

  const [draftFields, setDraftFields] = useState({});
  const setDraftField = useCallback((key, value) => setDraftFields((prev) => ({ ...prev, [key]: value })), []);

  // Clamp the viewed step back into visible after a branch swap hides it.
  useEffect(() => {
    if (steps.some((s) => s.id === viewedStepId)) return;
    setViewedStepId(findFallbackStep(viewedStepId, steps)?.id);
  }, [steps, viewedStepId]);

  const viewedIndex = steps.findIndex((s) => s.id === viewedStepId);
  const safeIndex = viewedIndex >= 0 ? viewedIndex : 0;
  const viewedStep = steps[safeIndex];
  const persistedIndex = steps.findIndex((s) => s.id === persistedStepId);
  const PanelComponent = STEP_COMPONENTS[viewedStep?.id];

  const handleSelect = (stepId) => {
    const idx = steps.findIndex((s) => s.id === stepId);
    if (idx === -1 || idx > persistedIndex) return;
    setViewedStepId(stepId);
  };
  const handleBack = () => {
    if (safeIndex > 0) setViewedStepId(steps[safeIndex - 1].id);
  };
  const advanceBlocker = getAdvanceBlocker(viewedStep?.id, installation, branch, dvrStore.dvrs);
  const identifier = installation?.uid ?? installation?.deal_id;
  const handleNext = async () => {
    if (safeIndex >= steps.length - 1 || advanceBlocker) return;
    const nextStep = steps[safeIndex + 1];
    breadcrumbStepChange(viewedStep?.id, nextStep.id, identifier);
    setViewedStepId(nextStep.id);
    // Awaited so the Next button stays disabled until the transition lands.
    if (isAfterInSequence(nextStep.id, installation?.step, branch)) {
      await commitField("step", nextStep.id);
    }
  };
  // Skip the delivery wait when the carrier flow doesn't apply (e.g. resellers).
  const canSkipShipping = branch === "local" && viewedStep?.id === "logistics";
  const handleSkipShipping = async () => {
    if (safeIndex >= steps.length - 1) return;
    const nextStep = steps[safeIndex + 1];
    breadcrumbStepChange(viewedStep?.id, nextStep.id, identifier);
    setViewedStepId(nextStep.id);
    // Commit the next visible step (past the non-visible "delivered" marker) so
    // persisted and viewed stay in sync, same as handleNext.
    if (isAfterInSequence(nextStep.id, installation?.step, branch)) {
      await commitField("step", nextStep.id);
    }
  };

  const bottomNav = (
    <WizardBottomNav
      onBack={handleBack}
      onNext={handleNext}
      onSkip={canSkipShipping ? handleSkipShipping : undefined}
      skipLabel="Skip shipment"
      isFirst={safeIndex === 0}
      isLast={safeIndex === steps.length - 1}
      nextDisabled={Boolean(advanceBlocker)}
      nextHelp={advanceBlocker}
      nextButton={buildCustomNextButton(viewedStep?.id, {
        onClick: handleNext,
        disabled: Boolean(advanceBlocker),
      })}
    />
  );

  const status = <SaveStatusBadge status={saveStatus} />;

  return (
    <Card>
      <CardBody className="p-0">
        <div className="d-flex" style={{ minHeight: "32rem" }}>
          <VerticalStepper
            steps={steps}
            viewedStepId={viewedStep?.id}
            persistedStepId={persistedStepId}
            onSelect={handleSelect}
          />
          {PanelComponent ? (
            <PanelComponent
              installation={installation}
              setField={setField}
              commitField={commitField}
              mergeServerFields={mergeServerFields}
              flushPending={flushPending}
              dvrStore={dvrStore}
              draftFields={draftFields}
              setDraftField={setDraftField}
              status={status}
              bottomNav={bottomNav}
            />
          ) : (
            <div className="flex-grow-1 px-4 py-3">Unknown step.</div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default WizardCard;
